import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Globe, Instagram, Compass, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Overline, TourCard, img } from "@/components/Cards";
import FollowButton from "@/components/FollowButton";

export default function OperatorProfile() {
  const { slug } = useParams();
  const [op, setOp] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setOp(null); setNotFound(false);
    api.get(`/operators/${slug}`).then((r) => setOp(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-400">Operator not found. <Link to="/tours" className="text-[#00E5FF]">Browse tours</Link></div>;
  if (!op) return <div className="py-32 text-center text-neutral-500">Loading...</div>;

  return (
    <div data-testid="operator-profile">
      <Seo title={`${op.name} — RiNo Tour Operator`} description={op.tagline || op.bio?.slice(0, 155)} image={op.logo} />

      <div className="border-b border-white/10 bg-gradient-to-br from-[#04141a] to-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <Link to="/tours" className="flex w-fit items-center gap-2 text-neutral-300 hover:text-white text-sm mb-6"><ArrowLeft size={16} /> Tours</Link>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {op.logo
              ? <img src={op.logo} alt={op.name} className="w-24 h-24 object-cover border border-white/15 flex-shrink-0" data-testid="operator-logo" />
              : <div className="w-24 h-24 bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0"><Compass size={34} className="text-[#00E5FF]" /></div>}
            <div className="flex-1">
              <Overline>Tour & Experience Operator</Overline>
              <h1 className="font-display font-black text-4xl sm:text-5xl text-white mt-1" data-testid="operator-name">{op.name}</h1>
              {op.tagline && <p className="text-[#FFEA00] text-base mt-2">{op.tagline}</p>}
              {op.specialties?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {op.specialties.map((s, i) => <span key={i} className="text-xs font-accent border border-white/15 text-neutral-300 px-3 py-1">{s}</span>)}
                </div>
              )}
              <div className="flex flex-wrap gap-4 mt-4 items-center">
                {op.website && <a href={op.website} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] text-sm flex items-center gap-1.5 hover:underline"><Globe size={15} /> Website</a>}
                {op.instagram && <a href={op.instagram.startsWith("http") ? op.instagram : `https://instagram.com/${op.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] text-sm flex items-center gap-1.5 hover:underline"><Instagram size={15} /> Instagram</a>}
                <FollowButton kind="operator" subjectKey={op.slug} label={op.name} className="px-5 py-2" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {op.bio && (
          <div className="mb-12 max-w-3xl">
            <h2 className="font-display font-bold text-2xl text-white mb-3">About</h2>
            <p className="text-neutral-300 text-lg leading-relaxed whitespace-pre-line" data-testid="operator-bio">{op.bio}</p>
          </div>
        )}

        <h2 className="font-display font-bold text-2xl text-white mb-5">Tours & Experiences <span className="text-neutral-500 text-base">({op.tours?.length || 0})</span></h2>
        {op.tours?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="operator-tours">
            {op.tours.map((t, i) => <TourCard key={t.slug} t={t} index={i} />)}
          </div>
        ) : (
          <div className="text-neutral-500 border border-white/10 py-12 text-center">No tours listed yet.</div>
        )}
      </div>
    </div>
  );
}
